import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import apiClient from '../api/client';
import {
  normalizePhoneInput,
  validateEmailCom,
  validatePhone10Digits,
} from '../utils/formValidation';
import PhoneInput from '../components/PhoneInput';
import PageTemplate from './PageTemplate';
import './ModulePage.css';

function AddTeacher() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    course: '',
    password: '',
    confirmPassword: '',
    role: 'Teacher',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadCourses() {
      setLoadingCourses(true);
      try {
        const res = await apiClient.get('/api/courses/');
        if (cancelled) return;

        const list = Array.isArray(res.data)
          ? res.data
          : res.data?.courses?.results || res.data?.courses || res.data?.results || [];
        setCourses(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load courses for teacher form:', err);
          setCourses([]);
        }
      } finally {
        if (!cancelled) setLoadingCourses(false);
      }
    }

    loadCourses();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'phone' ? normalizePhoneInput(value) : value;
    setForm((prev) => ({ ...prev, [name]: nextValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const trimmed = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      password: form.password,
      confirmPassword: form.confirmPassword,
    };

    if (!trimmed.name || !trimmed.email || !trimmed.phone || !trimmed.password || !trimmed.confirmPassword) {
      setError('Please fill in all required fields.');
      return;
    }

    const emailError = validateEmailCom(trimmed.email);
    if (emailError) {
      setError(emailError);
      return;
    }

    const phoneError = validatePhone10Digits(trimmed.phone);
    if (phoneError) {
      setError(phoneError);
      return;
    }

    if (trimmed.password.length < 5) {
      setError('Password should be at least 5 characters long.');
      return;
    }

    if (trimmed.password !== trimmed.confirmPassword) {
      setError('Password and Confirm Password must match.');
      return;
    }

    setSubmitting(true);

    try {
      await apiClient.post(
        '/api/auth/teachers/',
        {
          full_name: trimmed.name,
          email: trimmed.email,
          phone_number: normalizePhoneInput(trimmed.phone),
          course: form.course || undefined,
          password: trimmed.password,
          confirm_password: trimmed.confirmPassword,
          role: 'teacher',
        },
        user?.access
          ? {
              headers: {
                Authorization: `Bearer ${user.access}`,
              },
            }
          : undefined
      );

      setSuccess('Teacher added successfully.');
      setTimeout(() => navigate('/teachers', { replace: true }), 600);
    } catch (err) {
      if (err.response && err.response.data && err.response.data.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Unable to add teacher. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageTemplate
      title="Add Teacher"
      subtitle="Create a new teacher account for your LMS"
    >
      <div className="add-teacher-panel">
        <div className="add-teacher-header">
          <div>
            <h2>Teacher details</h2>
            <p>Fill in the basic information and credentials for the teacher.</p>
          </div>
          <span className="add-teacher-badge">Role: Teacher</span>
        </div>

        <form className="add-teacher-grid" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              id="name"
              name="name"
              type="text"
              placeholder="Enter teacher name"
              value={form.name}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="name@example.com"
              value={form.email}
              onChange={handleChange}
            />
          </div>

          <PhoneInput
            id="phone"
            label="Mobile Number"
            value={form.phone}
            onChange={(phone) => setForm((prev) => ({ ...prev, phone }))}
            required
            disabled={submitting}
          />

          <div className="form-group">
            <label htmlFor="course">Course (optional)</label>
            <select
              id="course"
              name="course"
              value={form.course}
              onChange={handleChange}
              className="select-input"
              disabled={loadingCourses}
            >
              <option value="">Choose a course</option>
              {courses.map((course) => (
                <option key={course.course_id || course.id} value={course.course_id || course.id}>
                  {course.course_name || course.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              placeholder="Enter password"
              value={form.password}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              placeholder="Re-enter password"
              value={form.confirmPassword}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Role</label>
            <input type="text" value={form.role} disabled />
          </div>

          {error && <p className="form-error">{error}</p>}
          {success && <p className="form-success">{success}</p>}

          <div className="add-teacher-actions">
            <button
              type="button"
              className="btn-primary btn-secondary"
              onClick={() => navigate('/teachers')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              disabled={submitting}
            >
              {submitting ? 'Saving…' : 'Save Teacher'}
            </button>
          </div>
        </form>
      </div>
    </PageTemplate>
  );
}

export default AddTeacher;

